"use client";

import { SquareKanbanIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../../cn";
import { Board, BoardColumn } from "../board";
import { EmptyState } from "../empty-state";
import { InlineAdd } from "./inline-add";
import { TaskCard } from "./task-card";
import type { StatusTone, TaskSummary } from "./task-meta";

// The board view (Work OS container-view BoardColumn/BoardCard, design spec §6.5): status
// columns 300 wide that scroll sideways, cards that drag between and within columns with a 2 px
// insertion line, collapsible columns, and "Add task" at each column's foot. Every drag has a
// keyboard twin: with a card focused, Alt+←/→ moves it to the next column and Alt+↑/↓ reorders
// it (the title is the focus stop, so pass `onTaskOpen` or task `href`s); a polite live region
// says where it went. The view only reports moves (`onTaskMove`);
// the caller reorders its data.

export type BoardViewColumn = {
  id: string;
  title: string;
  tone?: StatusTone;
  tasks: TaskSummary[];
};

export type BoardViewProps = Omit<React.ComponentProps<"section">, "children"> & {
  columns: BoardViewColumn[];
  selectedTaskId?: string;
  onTaskOpen?: (taskId: string) => void;
  /** `toIndex` is the position in the destination column once the card has left its origin. */
  onTaskMove?: (taskId: string, toColumnId: string, toIndex: number) => void;
  onAddTask?: (columnId: string, title: string) => void;
  /** Controlled collapsed columns; uncontrolled when omitted. */
  collapsedColumnIds?: string[];
  onCollapsedChange?: (columnId: string, collapsed: boolean) => void;
  empty?: React.ReactNode;
};

type Target = { columnId: string; index: number };

export function BoardView({
  columns,
  selectedTaskId,
  onTaskOpen,
  onTaskMove,
  onAddTask,
  collapsedColumnIds,
  onCollapsedChange,
  empty,
  className,
  "aria-label": ariaLabel = "Board",
  ...props
}: BoardViewProps) {
  const [ownCollapsed, setOwnCollapsed] = React.useState<string[]>([]);
  const collapsed = collapsedColumnIds ?? ownCollapsed;
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [target, setTarget] = React.useState<Target | null>(null);
  const [announcement, setAnnouncement] = React.useState("");
  const refocus = React.useRef<string | null>(null);
  const rootRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (!refocus.current) return;
    const card = rootRef.current?.querySelector<HTMLElement>(
      `[data-task-id="${CSS.escape(refocus.current)}"] [data-slot="board-card-title"]`,
    );
    if (card) {
      card.focus();
      refocus.current = null;
    }
  });

  const where = (taskId: string) => {
    for (const c of columns) {
      const i = c.tasks.findIndex((t) => t.id === taskId);
      if (i >= 0) return { column: c, index: i };
    }
    return null;
  };

  const move = (taskId: string, toColumnId: string, toIndex: number) => {
    const from = where(taskId);
    const to = columns.find((c) => c.id === toColumnId);
    if (!from || !to || !onTaskMove) return;
    if (from.column.id === toColumnId && from.index === toIndex) return;
    onTaskMove(taskId, toColumnId, toIndex);
    refocus.current = taskId;
    setAnnouncement(
      `Moved ${from.column.tasks[from.index]?.title ?? "task"} to ${to.title}, position ${toIndex + 1}.`,
    );
  };

  const setCollapsed = (columnId: string, value: boolean) => {
    if (!collapsedColumnIds)
      setOwnCollapsed((list) =>
        value ? [...list, columnId] : list.filter((id) => id !== columnId),
      );
    onCollapsedChange?.(columnId, value);
  };

  function onCardKeyDown(e: React.KeyboardEvent, taskId: string) {
    if (!e.altKey || !onTaskMove) return;
    const from = where(taskId);
    if (!from) return;
    if (from.column.tasks[from.index]?.locked) return; // TASK-LOCK: an agent is working on it
    const ci = columns.findIndex((c) => c.id === from.column.id);
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const next = columns[ci + (e.key === "ArrowLeft" ? -1 : 1)];
      if (!next) return;
      e.preventDefault();
      move(taskId, next.id, Math.min(from.index, next.tasks.length));
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      const index = from.index + (e.key === "ArrowUp" ? -1 : 1);
      if (index < 0 || index >= from.column.tasks.length) return;
      e.preventDefault();
      move(taskId, from.column.id, index);
    }
  }

  function onColumnDragOver(e: React.DragEvent<HTMLDivElement>, column: BoardViewColumn) {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const cards = Array.from(e.currentTarget.querySelectorAll<HTMLElement>("[data-board-item]"));
    let index = cards.length;
    for (let i = 0; i < cards.length; i++) {
      const r = cards[i]?.getBoundingClientRect();
      if (r && e.clientY < r.top + r.height / 2) {
        index = i;
        break;
      }
    }
    if (target?.columnId !== column.id || target.index !== index)
      setTarget({ columnId: column.id, index });
  }

  function onColumnDrop(e: React.DragEvent, column: BoardViewColumn) {
    e.preventDefault();
    const id = dragId ?? e.dataTransfer.getData("text/plain");
    const at = target?.columnId === column.id ? target.index : column.tasks.length;
    setDragId(null);
    setTarget(null);
    if (!id) return;
    const from = where(id);
    // Dropping below its own slot: the card leaves first, so the index shifts up by one.
    const index = from && from.column.id === column.id && from.index < at ? at - 1 : at;
    move(id, column.id, index);
  }

  if (columns.length === 0)
    return (
      <div data-slot="board-view" className={className}>
        {empty ?? <EmptyState icon={SquareKanbanIcon} title="No statuses yet" variant="card" />}
      </div>
    );

  return (
    <section
      ref={rootRef}
      data-slot="board-view"
      aria-label={ariaLabel}
      className={cn("flex min-h-0 flex-col", className)}
      {...props}
    >
      <Board className="min-h-0 flex-1 px-1">
        {columns.map((column) => {
          const isCollapsed = collapsed.includes(column.id);
          return (
            <BoardColumn
              key={column.id}
              title={column.title}
              count={column.tasks.length}
              collapsed={isCollapsed}
              onCollapsedChange={(v) => setCollapsed(column.id, v)}
              data-column-id={column.id}
              data-tone={column.tone}
              className={cn(
                column.tone &&
                  !isCollapsed &&
                  "[&_header_h3]:before:size-1.5 [&_header_h3]:before:rounded-full [&_header_h3]:before:content-[''] [&_header_h3]:gap-1.5",
                column.tone && !isCollapsed && TONE_BEFORE[column.tone],
              )}
            >
              {/* biome-ignore lint/a11y/noStaticElementInteractions: the drop zone of a pointer drag; Alt+arrows on the cards are its keyboard twin. */}
              <div
                data-slot="board-view-column-body"
                onDragOver={(e) => onColumnDragOver(e, column)}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setTarget(null);
                }}
                onDrop={(e) => onColumnDrop(e, column)}
                className={cn(
                  "flex min-h-16 flex-col rounded-lg pb-1",
                  dragId && target?.columnId === column.id && "bg-muted/60",
                )}
              >
                {column.tasks.map((task, i) => (
                  <React.Fragment key={task.id}>
                    {target?.columnId === column.id && target.index === i ? <DropLine /> : null}
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: drag source; the focusable title inside carries the keyboard moves. */}
                    <div
                      data-board-item=""
                      data-list-row=""
                      // TASK-LOCK: a card an agent is working on stays put (its title says why).
                      draggable={Boolean(onTaskMove) && !task.locked}
                      title={
                        task.locked
                          ? `${task.locked.by} is working on this since ${task.locked.since}`
                          : undefined
                      }
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", task.id);
                        setDragId(task.id);
                      }}
                      onDragEnd={() => {
                        setDragId(null);
                        setTarget(null);
                      }}
                      onKeyDown={(e) => onCardKeyDown(e, task.id)}
                    >
                      <TaskCard
                        task={task}
                        onOpen={onTaskOpen}
                        selected={task.id === selectedTaskId}
                        dragging={task.id === dragId}
                        aria-keyshortcuts={
                          onTaskMove
                            ? "Alt+ArrowLeft Alt+ArrowRight Alt+ArrowUp Alt+ArrowDown"
                            : undefined
                        }
                      />
                    </div>
                  </React.Fragment>
                ))}
                {target?.columnId === column.id && target.index === column.tasks.length ? (
                  <DropLine />
                ) : null}
                {onAddTask ? (
                  <InlineAdd
                    fieldLabel={`New task in ${column.title}`}
                    onAdd={(title) => onAddTask(column.id, title)}
                  />
                ) : null}
              </div>
            </BoardColumn>
          );
        })}
      </Board>
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </section>
  );
}

// Spelled out so Tailwind sees every class (same colours as TONE_DOT).
const TONE_BEFORE: Record<StatusTone, string> = {
  neutral: "[&_header_h3]:before:bg-border-strong",
  accent: "[&_header_h3]:before:bg-primary",
  success: "[&_header_h3]:before:bg-success",
  warning: "[&_header_h3]:before:bg-warning",
  danger: "[&_header_h3]:before:bg-destructive",
  info: "[&_header_h3]:before:bg-info",
};

function DropLine() {
  return <div aria-hidden={true} className="-mt-1 mb-1 h-0.5 rounded-full bg-primary" />;
}
